/**
 * Product analytics wrapper (PostHog).
 *
 * This module is the single place where analytics events and the business
 * logic around them are defined. UI components and contexts should call the
 * semantic helpers here (trackSignIn, trackSignUp, ...) rather than touching
 * `posthog` directly — that keeps PostHog out of the component tree and lets
 * us evolve the analytics layer in one spot.
 *
 * Automatic capture is off except for $pageview; everything else (autocapture,
 * pageleave, dead clicks, rageclicks, session recording, heatmaps, surveys,
 * performance, exceptions) is disabled. We add custom events gradually and
 * explicitly: give it a name in EVENTS and either add a dedicated helper or use
 * the generic `capture(event, props)`.
 *
 * Note on importing the singleton: the PostHog docs warn against importing
 * `posthog` directly in components (you should use the usePostHog hook so you
 * don't create a second, uninitialized client). That doesn't apply here — a
 * service module can't use hooks, and `initAnalytics()` runs once at app entry
 * (src/index.jsx) before any event can fire, so this is the one initialized
 * instance. Every method also no-ops until init completes, so analytics can
 * never throw or block auth.
 */
import posthog from 'posthog-js';

const POSTHOG_TOKEN = process.env.POSTHOG_PROJECT_TOKEN;
// Reverse proxy for ingestion; ad blockers don't recognize our own subdomain.
const POSTHOG_HOST = process.env.POSTHOG_HOST || 'https://p.ultimatewebscraper.com';
// PostHog's real host — needed so the toolbar and other UI features work.
const POSTHOG_UI_HOST = process.env.POSTHOG_UI_HOST || 'https://us.posthog.com';
const ANALYTICS_ENABLED = process.env.ANALYTICS_ENABLED !== 'false';

/** Canonical event names — keep events out of stringly-typed call sites. */
export const EVENTS = {
  SIGNED_IN: 'user_signed_in',
  SIGNED_UP: 'user_signed_up',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  CHECKOUT_CANCELLED: 'checkout_cancelled',
  CHECKOUT_FAILED: 'checkout_failed',
  CHECKOUT_WIZARD_VIEWED: 'checkout_wizard_viewed',
  EXTENSION_UNINSTALLED: 'extension_uninstalled',
  CONNECT_CLAUDE_VIEWED: 'connect_claude_viewed',
  CONNECT_CLAUDE_CTA_CLICKED: 'connect_claude_cta_clicked',
  CONNECT_CLAUDE_UPGRADE_CLICKED: 'connect_claude_upgrade_clicked',
  CLAUDE_CONNECT_STARTED: 'claude_connect_started',
  CLAUDE_CONSENT_VIEWED: 'claude_consent_viewed',
  CLAUDE_CONSENT_DECIDED: 'claude_consent_decided',
  CLAUDE_DISCONNECTED: 'claude_disconnected',
  // Consent-funnel moments (OAuthConsent.jsx). Like the claude_consent_* events
  // above, these fire for EVERY MCP client — segment by `client_host`.
  MCP_CONSENT_UPSELL_SHOWN: 'mcp_consent_upsell_shown',
  MCP_CONSENT_UPGRADE_CLICKED: 'mcp_consent_upgrade_clicked',
  MCP_CONSENT_PLAN_RECHECK: 'mcp_consent_plan_recheck',
  MCP_CONSENT_ASK_OWNER_COPIED: 'mcp_consent_ask_owner_copied',
  // Correction signal for claude_consent_decided: that event fires BEFORE the
  // decision request (the approve path redirects away), so a server-rejected
  // decision still counted as decided. Subtract these when reading approvals.
  MCP_CONSENT_DECIDE_FAILED: 'mcp_consent_decide_failed',
};

let initialized = false;

/**
 * Initialize PostHog with ALL automatic capture disabled.
 * No-ops if already initialized or if no project token is configured.
 */
export function initAnalytics() {
  if (initialized || !ANALYTICS_ENABLED || !POSTHOG_TOKEN) return;

  posthog.init(POSTHOG_TOKEN, {
    api_host: POSTHOG_HOST, // reverse proxy
    ui_host: POSTHOG_UI_HOST, // PostHog's real host
    defaults: '2026-01-30', // version pin; the explicit flags below override it

    // --- Automatic capture: only $pageview is on; everything else off ---
    autocapture: false, // no auto click/change/submit events
    capture_pageview: true, // automatic $pageview (incl. SPA navigation)
    capture_pageleave: false, // no automatic $pageleave
    capture_dead_clicks: false, // no $dead_click
    rageclick: false, // no $rageclick

    // --- Disable session recording / heatmaps / surveys ---
    disable_session_recording: true,
    capture_heatmaps: false,
    disable_surveys: true,

    // --- Disable performance / web vitals capture ---
    capture_performance: false,

    // --- Disable exception autocapture (also off by default; explicit for safety) ---
    capture_exceptions: false,

    // Only create person profiles once a user is identified.
    person_profiles: 'identified_only',

    // The OAuth consent URL carries the raw authorize query (client_id,
    // redirect_uri, state, code_challenge) — and the sign-in bounce carries the
    // SAME query URL-encoded inside /signin?returnTo=… . Scrub both forms from
    // every captured string property (event props plus the $set/$set_once
    // initial-URL person props) so none of it ever lands in analytics.
    sanitize_properties: (properties) => {
      const carriesConsentQuery = (v) => {
        const lower = v.toLowerCase();
        return lower.includes('/oauth/consent?') || lower.includes('%2foauth%2fconsent%3f');
      };
      const scrubStrings = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        for (const key of Object.keys(obj)) {
          const v = obj[key];
          if (typeof v === 'string' && carriesConsentQuery(v)) obj[key] = v.split('?')[0];
        }
      };
      if (properties) {
        scrubStrings(properties);
        scrubStrings(properties.$set);
        scrubStrings(properties.$set_once);
      }
      return properties;
    },
  });

  initialized = true;
}

/** Build the canonical person properties from the REAL user. */
function personProps(user) {
  return {
    email: user?.email,
    name: user?.name,
  };
}

/**
 * Identify the current user. Always pass the REAL `user`, never the
 * impersonated `effectiveUser`, so view-as never pollutes another profile.
 */
export function identifyUser(user) {
  if (!initialized || !user?.id) return;
  posthog.identify(String(user.id), personProps(user));
}

/** Returning user → existing account signed in. */
export function trackSignIn(user, { method } = {}) {
  if (!initialized) return;
  identifyUser(user);
  posthog.capture(EVENTS.SIGNED_IN, { method });
}

/** New account created. */
export function trackSignUp(user, { method } = {}) {
  if (!initialized) return;
  identifyUser(user);
  posthog.capture(EVENTS.SIGNED_UP, { method });
}

function workspaceGroupProps(workspace, subscription, credits) {
  return {
    name: workspace?.name, // special PostHog field — names the group in the UI
    role: workspace?.role,
    member_count: workspace?.member_count,
    plan: subscription?.plan?.name ?? 'free',
    plan_display_name: subscription?.plan?.display_name ?? 'Free',
    subscription_status: subscription?.status,
    billing_interval: subscription?.plan_stripe?.interval,
    cancel_at_period_end: subscription?.cancel_at_period_end,
    credits_available: credits ?? undefined,
  };
}

/**
 * Point analytics at the active workspace (group) and stamp plan-identity super
 * properties. Without `subscription` (eager, on switch) it re-registers from the
 * 'free' fallbacks so the prior workspace's plan never leaks onto new events.
 */
export function setWorkspaceGroup(workspace, subscription, credits) {
  if (!initialized || !workspace?.id) return;

  posthog.group('workspace', String(workspace.id), workspaceGroupProps(workspace, subscription, credits));

  // Status/cancel stay group-only — these are the user-filterable plan fields.
  posthog.register({
    workspace_id: String(workspace.id),
    workspace_role: workspace.role,
    plan: subscription?.plan?.name ?? 'free',
    plan_display_name: subscription?.plan?.display_name ?? 'Free',
    billing_interval: subscription?.plan_stripe?.interval,
  });
}

/**
 * Build the canonical checkout event payload from a plan object. Centralising it
 * here means call sites pass the raw `plan` (from getAvailablePlans) and the
 * analytics layer owns the price rule and property names — if the plan shape
 * changes, only this function changes.
 *
 * Accepts the rest of the event context (interval, source, flow, type, reason,
 * …) and merges it in, dropping undefined keys so events stay clean.
 */
function checkoutEventProps({ plan, interval, ...rest } = {}) {
  // `plan` may be a full plan object (preferred) or just a name string when
  // that's all the call site has (e.g. a Stripe-return URL param).
  const planObj = typeof plan === 'object' && plan !== null ? plan : null;
  const price =
    interval === 'yearly' ? (planObj?.yearly_price ?? planObj?.price) : planObj?.price;
  return clean({
    plan: planObj ? planObj.name : plan,
    plan_display_name: planObj?.display_name,
    interval,
    price,
    ...rest,
  });
}

/** Drop undefined values so events don't carry empty keys. */
function clean(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
}

/**
 * Checkout funnel events. The flow spans a Stripe redirect, so start and
 * completion land in separate page loads — they're stitched by PostHog person
 * identity (the user is always identified before reaching Stripe). `flow` tags
 * the entry point: 'wizard' (extension/external) or 'plans' (in-app upgrade).
 *
 * Each helper takes a context object — pass `plan` as the raw plan object and
 * the price/display-name are derived for you (see checkoutEventProps).
 */
export function trackCheckoutStarted(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CHECKOUT_STARTED, checkoutEventProps(context));
}

export function trackCheckoutCompleted(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CHECKOUT_COMPLETED, checkoutEventProps(context));
}

export function trackCheckoutCancelled(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CHECKOUT_CANCELLED, checkoutEventProps(context));
}

export function trackCheckoutFailed(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CHECKOUT_FAILED, checkoutEventProps(context));
}

/**
 * Checkout wizard landed. `authenticated` records whether the user was already
 * signed in (vs having to sign in / sign up first) — the top of the wizard
 * funnel and the segment for "auth required before checkout".
 */
export function trackCheckoutWizardViewed(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CHECKOUT_WIZARD_VIEWED, checkoutEventProps(context));
}

/**
 * Extension uninstalled. The /uninstall page is anonymous (no auth token reaches
 * it — the extension is already gone) and redirects to a feedback form, so this
 * is the ballpark uninstall metric, typically captured without an identified person.
 */
export function trackExtensionUninstalled(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.EXTENSION_UNINSTALLED, clean(context));
}

/**
 * Claude integration lifecycle. These track the connector's own funnel — opening
 * the connect wizard, the OAuth consent screen, and disconnecting — separate from
 * the /connect-claude marketing events (CONNECT_CLAUDE_*) above.
 *
 * `source` tags where the flow was launched ('integrations' | 'connect-claude' |
 * 'modal'); `state` reuses the wizard's derived step ('signin' | 'upsell' |
 * 'connect'). Undefined keys are dropped so events stay clean.
 */

/** User opened the Connect Claude wizard. */
export function trackClaudeConnectStarted(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CLAUDE_CONNECT_STARTED, clean(context));
}

/** OAuth consent screen shown to the user (the moment Claude asks for access). */
export function trackClaudeConsentViewed(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CLAUDE_CONSENT_VIEWED, clean(context));
}

/**
 * User decided on the consent screen. `approved` is the outcome; `accessLevel`
 * ('full' | 'read_only') and `paidPlan` describe what they granted.
 */
export function trackClaudeConsentDecided(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CLAUDE_CONSENT_DECIDED, clean(context));
}

/** User revoked a Claude connection from the Integrations card. */
export function trackClaudeDisconnected(context = {}) {
  if (!initialized) return;
  posthog.capture(EVENTS.CLAUDE_DISCONNECTED, clean(context));
}

/** Clear identity on logout so the next login starts clean. */
export function reset() {
  if (!initialized) return;
  posthog.reset();
}

/** Generic escape hatch for future events. */
export function capture(event, properties) {
  if (!initialized) return;
  posthog.capture(event, properties);
}

export default {
  initAnalytics,
  identifyUser,
  trackSignIn,
  trackSignUp,
  trackCheckoutStarted,
  trackCheckoutCompleted,
  trackCheckoutCancelled,
  trackCheckoutFailed,
  trackCheckoutWizardViewed,
  trackExtensionUninstalled,
  trackClaudeConnectStarted,
  trackClaudeConsentViewed,
  trackClaudeConsentDecided,
  trackClaudeDisconnected,
  setWorkspaceGroup,
  reset,
  capture,
  EVENTS,
};
